import React, { useState, useEffect, useRef } from "react";
import "./TrainingDevelopment.css";
import { useNavigate } from 'react-router-dom';
import TrainingHero from "./TrainingHero";
import TrainingWiseCode from "./TrainingWiseCode";
import HRCopilotFeatures from './HRCopilotFeatures';
import HRCopilotNewsSection from "./HRCopilotNewsSection";


const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="training-video-popup-overlay" onClick={onClose}>
      <div className="training-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="training-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="training-video-container">
          <video ref={videoRef} controls autoPlay className="training-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="training-video-popup-info">
          <h3>Training & Development Platform</h3>
          <p>See how our learning platform transforms employee development and skills growth</p>
        </div>
      </div>
    </div>
  );
};

const TrainingDevelopment = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("learning");
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

    const handleAccessRecruit = () => navigate('/access-recruit');


  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-group-of-people-in-a-business-training-44545-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  const handleAccessTraining = () => {
  // Handle training management access
  window.open('/training-platform', '_blank');
  console.log("Access training management");
};

const openTrainingVideoPopup = () => {
  // Open training management video popup
  window.open('/training-demo', '_blank');
  console.log("Open training management video");
};

const handleExploreTraining = () => {
  // Handle explore training integrations
  window.open('/training-integrations', '_blank');
  console.log("Explore training integrations");
};

  const features = [
    {
      icon: "🎓",
      title: "Learning Management",
      description: "Comprehensive LMS with course creation, assignment tracking, and progress monitoring for all employee training"
    },
    {
      icon: "📚",
      title: "Course Library",
      description: "Access to thousands of pre-built courses across business, technical, compliance, and soft skills"
    },
    {
      icon: "🎯",
      title: "Skills Development",
      description: "Personalized learning paths and skill gap analysis to drive targeted employee development"
    },
    {
      icon: "🏆",
      title: "Certification Tracking",
      description: "Automated certification management, renewal alerts, and compliance training tracking"
    },
    {
      icon: "📊",
      title: "Learning Analytics",
      description: "Advanced reporting on training effectiveness, completion rates, and skill development progress"
    },
    {
      icon: "🤝",
      title: "Mentorship Programs",
      description: "Structured mentorship matching, goal setting, and progress tracking for career development"
    }
  ];

const trainingStages = [
  {
    phase: "Assessment",
    icon: "📋",
    time: "1-2 days",
    title: "Skills Assessment & Planning",
    steps: [
      "Current skills inventory analysis",
      "Learning needs identification",
      "Training budget planning",
      "Success metrics definition"
    ]
  },
  {
    phase: "Design",
    icon: "🎨",
    time: "3-7 days",
    title: "Learning Program Design",
    steps: [
      "Custom curriculum development",
      "Content selection & curation",
      "Learning path configuration",
      "Engagement strategy planning"
    ]
  },
  {
    phase: "Implementation",
    icon: "⚡",
    time: "2-4 weeks",
    title: "Platform Launch & Enrollment",
    steps: [
      "Learning platform configuration",
      "User onboarding & training",
      "Automated enrollment setup",
      "Integration with HR systems"
    ]
  },
  {
    phase: "Optimization",
    icon: "📈",
    time: "Continuous",
    title: "Performance Tracking & Improvement",
    steps: [
      "Real-time progress monitoring",
      "Learning effectiveness analysis",
      "Program optimization recommendations",
      "ROI measurement & reporting"
    ]
  }
];

const trainingMetrics = [
  { name: "Technical Skills", progress: 85 },
  { name: "Leadership Training", progress: 78 },
  { name: "Compliance Courses", progress: 95 },
  { name: "Soft Skills", progress: 72 }
];

const trainingIntegrations = [
  "Workday", "BambooHR", "LinkedIn Learning", "Coursera", 
  "Udemy", "Microsoft Teams", "Slack", "Zoom", 
  "SAP SuccessFactors", "Oracle HCM", "Google Meet", "Pluralsight"
];

const trainingTabs = [
  { id: 'courses', label: 'Course Management' },
  { id: 'analytics', label: 'Learning Analytics' },
  { id: 'compliance', label: 'Compliance Training' },
  { id: 'skills', label: 'Skills Development' }
];

  const tabs = [
    {
      id: "learning",
      label: "LEARNING MANAGEMENT",
      content: {
        icon: "🎓",
        title: "Comprehensive Learning Platform",
        description: "Deliver engaging training experiences with our full-featured LMS supporting multiple content formats, interactive assessments, and progress tracking.",
        features: ["COURSE CREATION", "PROGRESS TRACKING", "ASSESSMENTS", "CERTIFICATIONS"],
        secondary: {
          icon: "📈",
          title: "Learning Analytics & Insights",
          description: "Measure training effectiveness with detailed analytics on completion rates, knowledge retention, and skill development impact."
        },
        buttonText: "EXPLORE LEARNING PLATFORM"
      }
    },
    {
      id: "skills",
      label: "SKILLS DEVELOPMENT →",
      content: {
        icon: "🎯",
        title: "Targeted Skills Growth",
        description: "Identify skill gaps and create personalized development paths with AI-powered recommendations and progress monitoring.",
        features: ["SKILLS ASSESSMENT", "DEVELOPMENT PLANS", "PROGRESS TRACKING", "CAREER PATHS"],
        secondary: {
          icon: "📊",
          title: "Skills Analytics & Reporting",
          description: "Track organizational skills development, identify competency gaps, and measure ROI on training investments."
        },
        buttonText: "EXPLORE SKILLS TOOLS"
      }
    },
    {
      id: "content",
      label: "CONTENT LIBRARY",
      content: {
        icon: "📚",
        title: "Extensive Course Catalog",
        description: "Access thousands of ready-to-use courses across leadership, technical skills, compliance, and professional development.",
        features: ["PRE-BUILT COURSES", "CUSTOM CONTENT", "MICROLEARNING", "MOBILE LEARNING"],
        secondary: {
          icon: "🔄",
          title: "Content Management System",
          description: "Easily create, organize, and update training materials with our intuitive content management platform."
        },
        buttonText: "EXPLORE CONTENT LIBRARY"
      }
    },
    {
      id: "mentorship",
      label: "MENTORSHIP & COACHING",
      content: {
        icon: "🤝",
        title: "Structured Development Programs",
        description: "Facilitate meaningful mentor-mentee relationships with automated matching, goal setting, and progress tracking.",
        features: ["MENTOR MATCHING", "GOAL SETTING", "PROGRESS TRACKING", "FEEDBACK TOOLS"],
        secondary: {
          icon: "🌟",
          title: "Leadership Development",
          description: "Build future leaders with structured coaching programs, leadership training, and succession planning tools."
        },
        buttonText: "EXPLORE MENTORSHIP"
      }
    }
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="training-container">
      {/* Scroll Progress Bar */}
      <div className="training-scroll-progress" style={{width: `${scrollProgress}%`}}></div>

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="training-background-animation">
        {/* <div className="training-floating-shape training-shape-1"></div>
        <div className="training-floating-shape training-shape-2"></div>
        <div className="training-floating-shape training-shape-3"></div>
        <div className="training-floating-shape training-shape-4"></div>
        <div className="training-floating-shape training-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="training-gradient-orb training-orb-1"></div>
        <div className="training-gradient-orb training-orb-2"></div>
        <div className="training-gradient-orb training-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="training-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="training-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(245, 158, 11, 0.08)" />
              <stop offset="50%" stopColor="rgba(16, 185, 129, 0.05)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0.08)" />
            </linearGradient>
            <linearGradient id="training-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(16, 185, 129, 0.06)" />
              <stop offset="50%" stopColor="rgba(245, 158, 11, 0.04)" />
              <stop offset="100%" stopColor="rgba(59, 130, 246, 0.06)" />
            </linearGradient>
            <pattern id="training-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#training-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#training-gradient1)"
            className="training-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#training-gradient2)"
            opacity="0.8"
            className="training-svg-path-2"
          />
        </svg>
      </div>

<TrainingHero/>
<TrainingWiseCode/>
<HRCopilotFeatures/>
<HRCopilotNewsSection/>

    </div>
  );
};

export default TrainingDevelopment;